import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'
import { notifications } from '@mantine/notifications'

const TOKEN_KEY = 'rustswarm_access_token'
const REFRESH_TOKEN_KEY = 'rustswarm_refresh_token'

// Create axios instance with default config
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Track if we're currently refreshing
let isRefreshing = false
let failedQueue: Array<{
  resolve: (value?: any) => void
  reject: (reason?: any) => void
}> = []

const processQueue = (error: any = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve()
    }
  })
  failedQueue = []
}

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token if available
    const token = localStorage.getItem(TOKEN_KEY)
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    return response
  },
  async (error) => {
    const originalRequest = error.config

    // Handle 401 errors with token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Skip refresh for login and refresh endpoints
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/refresh')
      ) {
        // Redirect to login
        window.location.href = '/login'
        return Promise.reject(error)
      }

      if (isRefreshing) {
        // Queue the request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(() => {
            return apiClient(originalRequest)
          })
          .catch((err) => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)

      if (!refreshToken) {
        // No refresh token, redirect to login
        isRefreshing = false
        window.location.href = '/login'
        return Promise.reject(error)
      }

      try {
        // Attempt to refresh the token
        const response = await axios.post('/api/v1/auth/refresh', {
          refresh_token: refreshToken,
        })

        const { access_token, refresh_token: new_refresh_token } = response.data

        // Update tokens
        localStorage.setItem(TOKEN_KEY, access_token)
        localStorage.setItem(REFRESH_TOKEN_KEY, new_refresh_token)

        // Update header for original request
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
        }

        processQueue(null)
        isRefreshing = false

        // Retry the original request
        return apiClient(originalRequest)
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect
        processQueue(refreshError)
        isRefreshing = false
        localStorage.removeItem(TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)

        notifications.show({
          title: 'Session Expired',
          message: 'Please log in again',
          color: 'red',
        })

        window.location.href = '/login'
        return Promise.reject(refreshError)
      }
    }

    // Handle other errors
    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 403:
          notifications.show({
            title: 'Permission Denied',
            message: data.error || 'You do not have permission to perform this action',
            color: 'red',
          })
          break

        case 404:
          // Don't show notification for 404s, let components handle it
          break

        case 500:
          notifications.show({
            title: 'Server Error',
            message: data.error || 'An internal server error occurred',
            color: 'red',
          })
          break

        default:
          if (status >= 400 && status !== 401) {
            notifications.show({
              title: `Error ${status}`,
              message: data.error || 'An error occurred',
              color: 'red',
            })
          }
      }
    } else if (error.request) {
      // Network error
      notifications.show({
        title: 'Network Error',
        message: 'Unable to connect to the server',
        color: 'red',
      })
    }

    return Promise.reject(error)
  }
)

export default apiClient